import config from '../config';

/**
 * SelectMultipleFilter needs to parse values from format
 * "has_all:a,b,c,d" or "a,b,c,d"
 * @param {string} uriComponentValue - The URI component value.
 * @returns {Array} - The parsed options.
 */
export const parseSelectFilterOptions = (uriComponentValue) => {
  const startsWithHasAll = uriComponentValue && uriComponentValue.indexOf('has_all:') === 0;
  const startsWithHasAny = uriComponentValue && uriComponentValue.indexOf('has_any:') === 0;

  if (startsWithHasAll) {
    return uriComponentValue.substring(8).split(',');
  } else if (startsWithHasAny) {
    return uriComponentValue.substring(8).split(',');
  } else {
    return uriComponentValue.split(',');
  }
};

/**
 * Check if any of the filters (defined by filterIds) have currently active query parameter in URL.
 * @param {Array} filterIds - The filter IDs.
 * @param {object} urlQueryParams - The URL query parameters.
 * @param {Array} filterConfigs - The filter configurations.
 * @returns {boolean} - True if any of the filters are active, false otherwise.
 */
export const isAnyFilterActive = (filterIds, urlQueryParams, filterConfigs) => {
  const getQueryParamKeysOfGivenFilters = (keys, config) => {
    const isFilterIncluded = filterIds.includes(config.id);
    const addedQueryParamNamesMaybe = isFilterIncluded ? config.queryParamNames : [];
    return [...keys, ...addedQueryParamNamesMaybe];
  };
  const queryParamKeysOfGivenFilters = filterConfigs.reduce(getQueryParamKeysOfGivenFilters, []);

  const paramEntries = Object.entries(urlQueryParams);
  const activeKey = paramEntries.find((entry) => {
    const [key, value] = entry;
    return queryParamKeysOfGivenFilters.includes(key) && value != null;
  });
  return !!activeKey;
};

/**
 * Check if the filter is currently active.
 * @param {string} filterId - The filter ID.
 * @param {Array} filters - The filters.
 * @returns {Array} - The options.
 */
export const findOptionsForSelectFilter = (filterId, filters) => {
  const filter = filters.find((f) => f.id === filterId);
  return filter && filter.config && filter.config.options ? filter.config.options : [];
};

// ================ amountOfSeats ================ //

const AMOUNT_OF_SEATS_FILTER_ID = 'amountOfSeats';
const AMOUNT_OF_SEATS_PARAM = 'pub_amountOfSeats';
const MIN_OPTION_KEY = /^min_(\d+)$/;

/**
 * Map the "Mindestanzahl Stellplätze" URL value onto the Flex range the search
 * API expects.
 *
 * `publicData.amountOfSeats` is registered as a `long` search schema, so Flex
 * takes an open range: `pub_amountOfSeats=3,` means "at least 3". That value
 * cannot live in the URL — `validURLParamForExtendedData` splits filter values
 * on `,` and intersects them with the configured option keys — so the URL
 * carries the option key `min_3` and this helper does the translation on the
 * way to the SDK.
 *
 * `SelectSingleFilterPlain` is a checkbox list despite its name, so the value
 * can hold several keys (`min_2,min_5`). Several checked boxes are a union, and
 * the union of "at least 2" and "at least 5" is "at least 2" — hence the
 * smallest selected minimum wins.
 *
 * @param {string|undefined|null} paramValue Raw `pub_amountOfSeats` URL value.
 * @param {Array} [filters] Filter configuration; defaults to the marketplace config.
 * @returns {object} `{ pub_amountOfSeats: '3,' }`, or `{}` when absent/unknown.
 */
export const amountOfSeatsSearchParams = (paramValue, filters = config.custom.filters) => {
  if (paramValue == null || paramValue === '') {
    return {};
  }
  const allowedKeys = findOptionsForSelectFilter(AMOUNT_OF_SEATS_FILTER_ID, filters).map(
    (o) => o.key,
  );
  const minimums = parseSelectFilterOptions(paramValue.toString())
    .filter((key) => allowedKeys.includes(key))
    .map((key) => Number(MIN_OPTION_KEY.exec(key)?.[1]))
    .filter((n) => Number.isInteger(n) && n > 0);

  return minimums.length > 0 ? { [AMOUNT_OF_SEATS_PARAM]: `${Math.min(...minimums)},` } : {};
};
